import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import usePagedRows from "../components/usePagedRows";
import Pagination from "../components/Pagination";
import { Badge } from "../components/Badge";
import {
  RefreshCw,
  Search,
  Banknote,
  Wallet,
  Hash,
  CheckCircle2,
  XCircle,
  Clock,
  Phone,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { k: "",       label: "All" },
  { k: "bkash",  label: "bKash" },
  { k: "nagad",  label: "Nagad" },
  { k: "rocket", label: "Rocket" },
];

const STATUS_TABS = [
  { k: "",          label: "Any status" },
  { k: "pending",   label: "Pending" },
  { k: "completed", label: "Completed" },
  { k: "failed",    label: "Failed" },
  { k: "rejected",  label: "Rejected" },
];

const CHANNEL_COLOR = {
  bkash:  "#e2136b",
  nagad:  "#ec1c24",
  rocket: "#8e2a85",
};

const PROVIDER_COLOR = {
  hrpay:    "#10b981",
  cowpay:   "#f59e0b",
  wpay:     "#3b82f6",
  starpago: "#a855f7",
};

const STATUS_BADGE = {
  pending:       { variant: "warning", icon: Clock,        label: "Pending"        },
  completed:     { variant: "success", icon: CheckCircle2, label: "Completed"      },
  failed:        { variant: "danger",  icon: XCircle,      label: "Failed"         },
  rejected:      { variant: "danger",  icon: XCircle,      label: "Rejected"       },
  auto_rejected: { variant: "neutral", icon: XCircle,      label: "Auto-rejected"  },
};

const fmtBdt = (n) => `৳${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function AgentBdtDeposits() {
  const [tab, setTab] = useState("");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ count: 0, amount: 0, credited_amount: 0, pending: 0, completed: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [actionId, setActionId] = useState(null); // deposit id currently being credited/rejected
  const paged = usePagedRows(rows, 12, [tab, status, q]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab) params.set("channel", tab);
      if (status) params.set("status", status);
      if (q.trim()) params.set("search", q.trim());
      const { data } = await api.get(`/agent-bdt-deposits?${params.toString()}`);
      setRows(data?.items || []);
      setTotals(data?.totals || { count: 0, amount: 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, status]);

  const channelBreakdown = useMemo(() => {
    const out = { bkash: 0, nagad: 0, rocket: 0 };
    rows.forEach((r) => {
      if (r.status !== "completed") return;
      const ch = r.channel || r.gateway_method;
      if (out[ch] !== undefined) out[ch] += Number(r.credited_amount || r.amount || 0);
    });
    return out;
  }, [rows]);

  const credit = async (r) => {
    const note = window.prompt(
      `Credit ৳${Number(r.amount || 0).toLocaleString()} to ${r.agent_username}?\n\nOptional note for the audit log:`,
      "",
    );
    if (note === null) return;
    setActionId(r.id);
    try {
      await api.post(`/agent-bdt-deposits/${r.id}/credit`, { note });
      toast.success("Deposit credited");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to credit");
    } finally {
      setActionId(null);
    }
  };

  const reject = async (r) => {
    const note = window.prompt(
      `Reject this pending deposit (৳${Number(r.amount || 0).toLocaleString()} via ${r.gateway_label || r.provider})?\n\nReason / note:`,
      "",
    );
    if (note === null) return;
    setActionId(r.id);
    try {
      await api.post(`/agent-bdt-deposits/${r.id}/reject`, { note });
      toast.success("Deposit rejected");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reject");
    } finally {
      setActionId(null);
    }
  };

  return (
    <div data-testid="agent-bdt-deposits-page">
      <PageHeader
        title="Agent BDT Deposits"
        subtitle="Gateway-driven bKash / Nagad / Rocket top-ups by agents via HRpay / Cowpay / WPay / StarPago / TarsPay. Balance auto-credits on webhook confirmation. Admins can manually credit a stuck-pending deposit or reject an abandoned one."
        actions={
          <div className="flex items-center gap-2">
            <a
              href="/root/admin/configuration/agent-bdt-deposit"
              className="h-9 px-3 inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-emerald-300 rounded-sm text-[13px]"
              data-testid="bdt-admin-go-settings"
            >
              Settings
            </a>
            <button
              onClick={load}
              className="h-9 px-3 inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-emerald-300 rounded-sm text-[13px]"
              data-testid="bdt-admin-refresh"
            >
              <RefreshCw size={13} /> Refresh
            </button>
          </div>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
        <StatCard label="Total submissions" value={totals.count}            icon={Hash}          color="#3b82f6" testid="bdt-stat-count" />
        <StatCard label="Credited"          value={fmtBdt(totals.credited_amount || 0)} icon={Wallet}        color="#10b981" testid="bdt-stat-credited" />
        <StatCard label="Pending"           value={totals.pending || 0}     icon={Clock}         color="#f59e0b" testid="bdt-stat-pending" />
        <StatCard label="Completed"         value={totals.completed || 0}   icon={CheckCircle2}  color="#10b981" testid="bdt-stat-completed" />
        <StatCard label="Failed/Rejected"   value={totals.failed || 0}      icon={XCircle}       color="#ef4444" testid="bdt-stat-failed" />
      </div>

      <div className="bg-white border border-gray-200 rounded-sm mb-4">
        {/* filters */}
        <div className="border-b border-gray-100 px-2 py-2 flex items-center gap-1 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.k || "all"}
              onClick={() => setTab(t.k)}
              data-testid={`bdt-admin-tab-${t.k || "all"}`}
              className={`px-3 py-1.5 rounded-sm text-[13px] font-semibold ${
                tab === t.k ? "bg-emerald-500 text-white" : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {t.label}
            </button>
          ))}
          <div className="h-5 w-px bg-gray-200 mx-1" />
          {STATUS_TABS.map((s) => (
            <button
              key={s.k || "all-status"}
              onClick={() => setStatus(s.k)}
              data-testid={`bdt-admin-status-${s.k || "all"}`}
              className={`px-3 py-1.5 rounded-sm text-[13px] font-semibold ${
                status === s.k ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {s.label}
            </button>
          ))}
          <div className="flex-1" />
          <form
            onSubmit={(e) => { e.preventDefault(); load(); }}
            className="flex items-center gap-1 border border-gray-200 rounded-sm px-2 h-8"
          >
            <Search size={13} className="text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search agent / reference"
              className="text-[13px] w-56 outline-none bg-transparent"
              data-testid="bdt-admin-search"
            />
          </form>
        </div>

        {/* secondary channel breakdown line */}
        <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-3 flex-wrap text-[11.5px] text-gray-600">
          <span className="text-gray-400 uppercase tracking-[1.5px] font-bold text-[10px]">Credited by wallet:</span>
          <ChannelChip label="bKash" value={fmtBdt(channelBreakdown.bkash)} color={CHANNEL_COLOR.bkash} />
          <ChannelChip label="Nagad" value={fmtBdt(channelBreakdown.nagad)} color={CHANNEL_COLOR.nagad} />
          <ChannelChip label="Rocket" value={fmtBdt(channelBreakdown.rocket)} color={CHANNEL_COLOR.rocket} />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left  py-2.5 px-3 font-semibold">Date</th>
                <th className="text-left  py-2.5 px-3 font-semibold">Agent</th>
                <th className="text-left  py-2.5 px-3 font-semibold">Wallet</th>
                <th className="text-left  py-2.5 px-3 font-semibold">Gateway</th>
                <th className="text-left  py-2.5 px-3 font-semibold">Reference</th>
                <th className="text-right py-2.5 px-3 font-semibold">Amount (BDT)</th>
                <th className="text-left  py-2.5 px-3 font-semibold">Status</th>
                <th className="text-right py-2.5 px-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="py-8 text-center text-gray-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="py-8 text-center text-gray-400">No records</td></tr>
              ) : (
                paged.visible.map((r) => {
                  const ch = r.channel || r.gateway_method;
                  const rawStatus = r.status || "pending";
                  // Distinguish system auto-reject from manual admin reject
                  const stKey = (rawStatus === "rejected" && r.auto_rejected) ? "auto_rejected" : rawStatus;
                  const st = STATUS_BADGE[stKey] || STATUS_BADGE.pending;
                  const StIcon = st.icon;
                  const provColor = PROVIDER_COLOR[r.provider] || "#777";
                  return (
                    <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50/60" data-testid={`bdt-admin-row-${r.id}`}>
                      <td className="py-2 px-3 whitespace-nowrap text-gray-600">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="py-2 px-3">
                        <div className="font-mono text-emerald-700 font-semibold" data-testid={`bdt-admin-row-${r.id}-agent`}>{r.agent_username || "—"}</div>
                        <div className="text-[11px] text-gray-500">{r.agent_name}</div>
                      </td>
                      <td className="py-2 px-3">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider"
                          style={{
                            background: `${CHANNEL_COLOR[ch] || "#999"}22`,
                            color: CHANNEL_COLOR[ch] || "#666",
                          }}
                        >
                          <Phone size={9} /> {r.channel_label || ch}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider"
                          style={{ background: `${provColor}1f`, color: provColor }}
                        >
                          {(r.provider || "").toUpperCase()}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <div className="font-mono text-gray-700 text-[11.5px] break-all" title={r.reference}>{r.reference || r.id.slice(0, 14)}</div>
                        {stKey === "pending" && r.redirect_url && (
                          <a
                            href={r.redirect_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 mt-0.5 text-[10.5px] text-amber-600 hover:text-amber-700"
                            data-testid={`bdt-admin-row-${r.id}-link`}
                          >
                            <ExternalLink size={10} /> open gateway
                          </a>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right font-mono font-bold text-emerald-700">{fmtBdt(r.credited_amount || r.amount)}</td>
                      <td className="py-2 px-3">
                        <Badge variant={st.variant}>
                          <span className="inline-flex items-center gap-1">
                            <StIcon size={10} /> {st.label}
                          </span>
                        </Badge>
                        {r.admin_note && (
                          <div className="text-[10.5px] text-gray-500 mt-0.5 italic" title={r.admin_note}>
                            Admin note: {r.admin_note.slice(0, 30)}{r.admin_note.length > 30 ? "…" : ""}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        {stKey === "pending" ? (
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              disabled={actionId === r.id}
                              onClick={() => credit(r)}
                              className="h-7 px-2 inline-flex items-center gap-1 rounded-sm text-[11px] font-semibold bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
                              data-testid={`bdt-admin-row-${r.id}-credit`}
                            >
                              {actionId === r.id ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                              Credit
                            </button>
                            <button
                              type="button"
                              disabled={actionId === r.id}
                              onClick={() => reject(r)}
                              className="h-7 px-2 inline-flex items-center gap-1 rounded-sm text-[11px] font-semibold bg-white border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                              data-testid={`bdt-admin-row-${r.id}-reject`}
                            >
                              <XCircle size={11} /> Reject
                            </button>
                          </div>
                        ) : (
                          <span className="text-[11px] text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <Pagination total={paged.totalRows} page={paged.page} pageSize={paged.pageSize} onChange={paged.setPage} testIdPrefix="agent-bdt-pg" />
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        <div className="h-7 w-7 rounded grid place-items-center" style={{ background: `${color}1a`, color }}>
          <Icon size={14} />
        </div>
        <div className="text-[10px] uppercase tracking-[1.4px] text-gray-500 font-semibold">{label}</div>
      </div>
      <div className="mt-1.5 font-mono font-bold text-[16px] text-gray-900 tabular-nums">{value}</div>
    </div>
  );
}

function ChannelChip({ label, value, color }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      <span className="font-semibold text-gray-800">{label}</span>
      <span className="font-mono text-gray-600">{value}</span>
    </span>
  );
}

// Banknote unused import retained for future use
void Banknote;
